import { useState, useEffect } from 'react';
import SolicitorHeader from '../../components/headers/SolicitorHeader';
import HistorySearchSidebar from '../../components/HistorySearchSidebar';

import iconTitle from '../../assets/img/icon-square.svg';
import openIcon from '../../assets/img/open-icon.svg';
import '../../styles/AccountCreate.css';
import '../../styles/Tabs.css';
import '../../styles/button.css';
import '../../styles/InsuranceApplicationList.css';
import '../../styles/InsuranceSystemSearch.css';
import '../../styles/SearchTabs.css';
import '../../styles/SearchHistory.css';
import { searchSearchHistory, toggleFavoriteSearchHistory, validateSearchHistoryRestore } from '../../services/api/AiSearchService';
import { getInsuranceTypeName, getDocumentTypeName } from '../../utils/insuranceTypeUtils';
import ReactPaginate from 'react-paginate';
import { showGlobalErrorDialog } from '../../providers/ErrorDialogProvider';

const SearchHistory = () => {
  const defaultParams = {
    searchDateFrom: '',
    searchDateTo: '',
    insuranceType: 1, // 生命保険を初期値に
    documentType: '',
    insuranceCompanies: [],
    productTypes: [],
    dateFrom: '',
    dateTo: '',
    searchQuery: '',
    isFavorite: false
  }

  const [searchParams, setSearchParams] = useState(defaultParams);

  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [sidebarActive, setSidebarActive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoreChecking, setIsRestoreChecking] = useState(false);
  const [, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('life'); // 'life' | 'sonpo'

  const [start, setStart] = useState(0);
  const perPage = 50;

  // Toggle sidebar minimized/maximized state
  const toggleSidebarMinimized = () => {
    setSidebarMinimized(!sidebarMinimized);
    // Always keep visible
    setSidebarVisible(true);
  };
  // Toggle sidebar visibility/hiding - full toggle
  const toggleSidebar = () => {
    if (isMobile) {
      // On mobile, toggle active state (slide up/down)
      setSidebarActive(!sidebarActive);
    } else {
      setSidebarVisible(!sidebarVisible);
      // When showing sidebar, also un-minimize it
      if (!sidebarVisible) {
        setSidebarMinimized(false);
      }
    }
  };

  // Screen size monitoring to update mobile detection
  useEffect(() => {

    const checkIsMobile = () => {
      const newIsMobile = window.innerWidth < 768; // 768px未満をモバイルと判断
      setIsMobile(newIsMobile);

      if (newIsMobile) {
        // 768px未満の場合、サイドバーを非表示に
        setSidebarVisible(false);
      } else {
        // 768px以上の場合、サイドバーを表示状態にリセット
        setSidebarVisible(true);
        setSidebarActive(false); // モバイル用のアクティブ状態もリセット
      }
    };

    // Initial check
    checkIsMobile();

    // Check on resize
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Function to handle search submission
  const handleSearch = async () => {

    const validationError = validateSearchParams(searchParams);

    // バリデーションエラーがある場合はアラート表示して処理を中断
    if (validationError) {
      alert(validationError);
      return;
    }

    setIsLoading(true);

    await executeSearchWithParams(searchParams);
  };

  const executeSearchWithParams = async (params) => {
    setIsLoading(true);
    try {
      const param = {
        searchDateFrom: params.searchDateFrom,
        searchDateTo: params.searchDateTo,
        insuranceType: params.insuranceType,
        documentType: params.documentType,
        insuranceCompanies: params.insuranceCompanies.map(company => company.indexName), // 保険会社はindexNameをAPIに送る
        productTypes: params.productTypes,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        query: params.searchQuery,
        isFavorite: params.isFavorite
      }
      const response = await searchSearchHistory(param);

      setSearchResults(response);
      setStart(0); // ページネーションの初期化
    } finally {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
      setIsLoading(false);
    }
  }

  // 検索条件リセット処理
  const handleReset = (insuranceType) => {
    // 検索条件を初期値にリセット
    setSearchParams({ ...defaultParams, insuranceType: insuranceType });
    console.log('検索条件をリセットしました');
  };

  const handleFavoriteToggle = async (resultId) => {
    // 楽観的にUIを先に更新し、失敗時にロールバックする
    setSearchResults(prevResults =>
      prevResults.map(result =>
        result.id === resultId ? { ...result, is_favorite: !result.is_favorite } : result
      )
    );
    try {
      await toggleFavoriteSearchHistory(resultId);
    } catch (error) {
      // エラーが発生した場合はUIを元に戻す
      setSearchResults(prevResults =>
        prevResults.map(result =>
          result.id === resultId ? { ...result, is_favorite: !result.is_favorite } : result
        )
      );
      alert('お気に入りの更新に失敗しました。時間をおいて再度お試しください。')
    }
  };

  const pageChange = (data) => {
    setStart(data.selected * perPage);
  }

  /**
   * 検索条件の復元処理
   * @param {number} historyId 検索履歴ID
   * @returns {Promise<void>}
   */
  const handleRestore = async (historyId) => {
    if (isRestoreChecking) return;

    // iOSのポップアップブロッカー対策：先に空のタブを開く（イベント発火と同じコンテキストで実行）
    const newWindow = window.open('about:blank', '_blank');
    if (newWindow) {
      newWindow.opener = null; // セキュリティ対策：新しいタブから元のページへのアクセスを防止
    }

    // セッションタイムアウト時にタブを閉じるリスナーを設定
    const sessionExpiredHandler = () => {
      newWindow?.close();
      window.removeEventListener('sessionExpired', sessionExpiredHandler);
      setIsRestoreChecking(false);
    };
    window.addEventListener('sessionExpired', sessionExpiredHandler);

    setIsRestoreChecking(true);
    try {
      // 復元前にサーバー側で条件の妥当性を確認する
      const validateResponse = await validateSearchHistoryRestore(historyId);
      if (!validateResponse?.valid) {
        // 検証失敗時は開いたタブを閉じる
        newWindow?.close();
        showGlobalErrorDialog({
          statusCode: -1, // 汎用エラーを表示するためのダミーコード
          message: validateResponse?.reasons || `検索条件を復元できません。`
        });
        return;
      }

      // 妥当性が確認できたら、開いたタブのURLを設定
      if (newWindow) {
        newWindow.location.href = `/solicitor/insurance/system/search?historyId=${historyId}`;
      }
    } catch (error) {
      // エラー時も開いたタブを閉じる
      newWindow?.close();
      showGlobalErrorDialog({
        statusCode: -1, // 汎用エラーを表示するためのダミーコード
        message: `再検索に失敗しました。`
      });
    } finally {
      // リスナーをクリーンアップ
      window.removeEventListener('sessionExpired', sessionExpiredHandler);
      setIsRestoreChecking(false);
    }
  };

  const validateSearchParams = (searchParams) => {
    const errors = [];

    // 保険種別のバリデーション
    if (!searchParams.insuranceType) {
      errors.push('・生命保険または損害保険を選択してください。');
    }

    // 検索日付のバリデーション
    if (searchParams.searchDateFrom && isNaN(new Date(searchParams.searchDateFrom).getTime())) {
      errors.push('・検索日時（開始）は有効な日付形式で入力してください。');
    }
    if (searchParams.searchDateTo && isNaN(new Date(searchParams.searchDateTo).getTime())) {
      errors.push('・検索日時（終了）は有効な日付形式で入力してください。');
    }
    if (searchParams.searchDateFrom && searchParams.searchDateTo) {
      if (new Date(searchParams.searchDateFrom) > new Date(searchParams.searchDateTo)) {
        errors.push('・検索日時は、開始日が終了日より前である必要があります。');
      }
    }

    // 加入日付のバリデーション
    if (searchParams.dateFrom && isNaN(new Date(searchParams.dateFrom).getTime())) {
      errors.push('・加入時期_開始は有効な日付形式で入力してください。');
    }
    if (searchParams.dateTo && isNaN(new Date(searchParams.dateTo).getTime())) {
      errors.push('・加入時期_終了は有効な日付形式で入力してください。');
    }
    if (searchParams.dateFrom && searchParams.dateTo) {
      if (new Date(searchParams.dateFrom) > new Date(searchParams.dateTo)) {
        errors.push('・加入時期は、開始日が終了日より前である必要があります。');
      }
    }

    // エラーがある場合は改行で結合して返す
    if (errors.length > 0) {
      return '以下の項目を確認してください：\n\n' + errors.join('\n');
    }

    return null; // エラーがない場合はnullを返す
  }

  return (
    <div className="insurance-search-page">
      {/* SolicitorHeader */}
      <SolicitorHeader onMenuToggle={(isOpen) => {
        // Menu state change handler can be implemented here
      }} />
      {/* Mobile sidebar toggle button */}
      {isMobile && (
        <div className="sidebar-toggle-container">
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <span className="c-display-flex c-display-flex--gap10">
              <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="c-mr-10">
                <path d="M0 2H10.5" stroke="white" />
                <path d="M13.5 2H14.5" stroke="white" />
                <circle cx="12" cy="2" r="1.5" stroke="white" />
                <path d="M0 12L8.5 12" stroke="white" />
                <path d="M11.5 12L14.5 12" stroke="white" />
                <circle cx="10" cy="12" r="1.5" stroke="white" />
                <path d="M0 7L4 7" stroke="white" />
                <path d="M6.5 7H14.5" stroke="white" />
                <circle cx="5" cy="7" r="1.5" stroke="white" />
              </svg>
              {sidebarActive ? '閉じる' : '文書検索'}
            </span>
            <span className={`toggle-icon ${sidebarActive ? 'open' : ''}`}></span>
          </button>
        </div>
      )}
      {/* Main content */}
      <div className="page-container">
        <div className={`insurance-layout history ${isMobile ? 'mobile' : ''}`}>
          {/* Left sidebar with visibility/state control */}
          {(sidebarVisible || isMobile) && (
            <HistorySearchSidebar
              isMobile={isMobile}
              sidebarMinimized={sidebarMinimized}
              sidebarActive={sidebarActive}
              toggleSidebarMinimized={toggleSidebarMinimized}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              onSearch={handleSearch}
              onReset={handleReset}
              isLoading={isLoading}
            />
          )}
          <main className={`insurance-results ${isMobile || (!sidebarVisible || sidebarMinimized) ? 'fullwidth' : ''}`}>
            <div className='page-header'>
              <div className='title-section'>
                <div className="icon-square">
                  <img src={iconTitle} alt="タイトルアイコン" />
                </div>
                <h1>検索履歴検索結果一覧</h1>
              </div>
            </div>
            {/* Loading and error states */}
            {isLoading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>検索中...</p>
              </div>
            )}
            {/* Empty state */}
            {!isLoading && searchResults.length === 0 && (
              <div className="empty-results">
                <p>検索結果がありません。検索条件を変更してください。</p>
              </div>
            )}

            {!isLoading && searchResults.length > 0 && (
              <div className="results-table-container">
                <table className='results-table'>
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>検索日時</th>
                      <th>生保/損保</th>
                      <th>文書種別</th>
                      <th>保険会社</th>
                      <th>商品種別</th>
                      <th>加入時期_開始</th>
                      <th>加入時期_終了</th>
                      <th>検索ワード</th>
                      <th>お気に入り</th>
                      <th>再検索</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.slice(start, start + perPage).map((result, index) => (
                      <tr key={result.id}>
                        <td className='column-no'>{start + index + 1}</td>
                        <td className='column-search-datetime'>{result.search_datetime}</td>
                        <td className='column-insurance-type'>{getInsuranceTypeName(result.insurance_type)}</td>
                        <td className='column-document-type'>{getDocumentTypeName(result.insurance_type, result.document_type_code, result.document_type_name)}</td>
                        <td className='column-insurance-companies'>{result.companies}</td>
                        <td className='column-product-types'>{result.categories}</td>
                        <td className='column-date-range'>{result.date_from}</td>
                        <td className='column-date-range'>{result.date_to}</td>
                        <td className='column-query'>{result.query}</td>
                        <td className='column-favorite'>
                          {result.is_favorite ?
                            <div className="heart-icon" onClick={() => handleFavoriteToggle(result.id)}></div> :
                            <div className="heart-icon-not-favorite" onClick={() => handleFavoriteToggle(result.id)}></div>
                          }
                        </td>
                        <td className='column-re-search'>
                          <div className="re-search-container">
                            <img
                              src={openIcon}
                              alt="再検索"
                              className="re-search-icon"
                              onClick={() => handleRestore(result.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && searchResults.length > 0 && (
              <ReactPaginate
                pageCount={Math.ceil(searchResults.length / perPage)}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={pageChange}
                containerClassName={'pagination'}
                pageClassName='page-item'
                activeClassName={'search-history-page-active'}
                previousLabel={'前へ'}
                nextLabel={'次へ'}
                previousClassName='page-item'
                nextClassName='page-item'
                previousLinkClassName='page-link'
                nextLinkClassName='page-link'
                disabledClassName='search-history-page-disabled'
                breakLabel={'...'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
              />
            )}

          </main>
        </div>
      </div>
    </div>
  )
}

export default SearchHistory;